import { useMemo } from 'react';
import type {
  Field,
  FieldValue,
  FileFieldValue,
  Option
} from '@elfsight/widget-form-builder/widget';
import { useSettings } from '@wu/widget-core';
import { BOOKING_HIDDEN_FIELDS } from '../form/fields';

function formatFileField(value: FileFieldValue) {
  if (!Array.isArray(value)) return '';
  return value
    .filter((file) => file.uploaded)
    .map((file) => `<a href="${file.uploaded}">${file.name}</a>`)
    .join(', ');
}

function formatConsentField(value: string) {
  return value === 'Yes' ? value : 'No';
}

function formatMultiChoiceField(options: Option[], value: FieldValue) {
  if (!Array.isArray(value)) return '';
  return value
    .map(
      (optionID) =>
        options.find((option) => option.id === optionID)?.name ?? optionID
    )
    .join(', ');
}

function formatSingleChoiceField(options: Option[], value?: FieldValue) {
  if (value == null) return '';
  const found = options.find((option) => option.id === String(value));
  return found?.name ?? String(value);
}

function formatDefaultField(value?: FieldValue) {
  return String(value ?? '');
}

export function useCreateCalendarEventDescription(fieldsArray: Field[]) {
  const { fields } = useSettings();

  return useMemo(() => {
    const fieldSettingsById = Object.fromEntries(
      fields.map((setting) => [setting.id, setting])
    );

    const bookingHiddenFieldIds = BOOKING_HIDDEN_FIELDS.map(
      (field) => field.id
    );

    const formattedFieldStr = fieldsArray
      .filter(
        (field) =>
          field.visible !== false && !bookingHiddenFieldIds.includes(field.id)
      )
      .map((field) => {
        const currentField = fieldSettingsById[field.id];

        const resultValue = (() => {
          if (field.type === 'file') {
            return formatFileField(field.value as FileFieldValue);
          }

          if (field.type === 'consent') {
            field.name = field.name?.replace(/<div>(.*?)<\/div>/, '$1');
            return formatConsentField(field.value as string);
          }

          if (currentField?.options) {
            if (Array.isArray(field.value)) {
              return formatMultiChoiceField(currentField.options, field.value);
            }

            return formatSingleChoiceField(currentField.options, field.value);
          }

          return formatDefaultField(field.value);
        })();

        const emptyField = !resultValue || !field.name;

        return emptyField ? '' : `${field.name}: ${resultValue}`;
      })
      .filter(Boolean);

    return formattedFieldStr.join('\n');
  }, [fieldsArray, fields]);
}
